import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { SerializedError } from '@reduxjs/toolkit';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { LockedControl } from '../../music24/components/LockedControl/LockedControl';
import { useUpgradePrompt } from '../../music24/hooks/useUpgradePrompt';
import { SMLUpgradeFeature } from '../../music24/config/music24PlanCapabilities';
import styles from './SmartLinkPublishBar.module.scss';

interface SmartLinkPublishBarProps {
  used: number;
  limit: number;
  onPublish: () => void;
  isPublishing: boolean;
  publishError?: Error | SerializedError;
}

/**
 * Design n: the "usage caption · SmartLink publish bar" row — a meter under the page count, and
 * the Publish button locked, with the cheapest-plan chip, once the plan's page limit is reached.
 * Publishing over the limit is still refused by the server (`M24_LIMIT_PAGES`) and opens the same
 * prompt through the global error handler — this only stops the round trip the client can see
 * coming.
 */
const SmartLinkPublishBarComponent: React.FC<SmartLinkPublishBarProps> = ({
  used,
  limit,
  onPublish,
  isPublishing,
  publishError,
}) => {
  const intl = useIntl();
  const percent = useMemo(() => (limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 100), [used, limit]);
  const isAtLimit = used >= limit;
  const openUpgradePrompt = useUpgradePrompt();
  const openPagesPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.pages), [openUpgradePrompt]);
  const publishLabel = intl.formatMessage({
    id: 'app.sml.stats.chart.empty_draft.publish',
    defaultMessage: 'Publish page',
  });

  return (
    <div className={styles.bar}>
      <div className={styles.usage}>
        <span className={styles.fig}>
          {intl.formatMessage(
            { id: 'app.sml.smartlinks.pages.usage', defaultMessage: '{used} of {limit} published pages' },
            { used, limit }
          )}
        </span>
        <div className={styles.meter} role="progressbar" aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${percent}%` }} />
        </div>
      </div>
      {isAtLimit ? (
        <LockedControl
          variant="button"
          feature={SMLUpgradeFeature.pages}
          size="sm"
          onClick={openPagesPrompt}
          label={publishLabel}
        />
      ) : (
        <SMLButton size="sm" color="primary" onClick={onPublish} isLoading={isPublishing} error={publishError}>
          {publishLabel}
        </SMLButton>
      )}
    </div>
  );
};

export const SmartLinkPublishBar = TypedMemo(SmartLinkPublishBarComponent);
